<script lang="ts">
  import { onMount } from "svelte";
  import { Button } from "$ui/button";
  import { Input } from "$ui/input";
  import * as Select from "$ui/select";
  import { LoadingSpinner } from "$ui/loading-spinner";
  import { Plus, Pencil, Trash2, X, Check, AlertCircle } from "lucide-svelte";
  import { cn } from "$utils/ui";
  import { selectedOrgStore } from "$lib/stores/orgs";
  import {
    listCustomInfoTypes,
    createCustomInfoType,
    updateCustomInfoType,
    deleteCustomInfoType,
    toggleCustomInfoType,
  } from "$lib/services/customInfoTypes";
  import type {
    CustomInfoType,
    CreateCustomInfoTypeRequest,
    CustomInfoTypeDetectionType,
    CustomInfoTypeLikelihood,
  } from "$lib/models/base";
  import { toaster } from "$lib/utils/toaster";

  let customInfoTypes: CustomInfoType[] = $state([]);
  let loading = $state(true);
  let error = $state<string | null>(null);

  // Form state
  let showForm = $state(false);
  let editingId = $state<string | null>(null);
  let formData = $state<CreateCustomInfoTypeRequest>({
    name: "",
    displayName: "",
    description: "",
    detectionType: "regex",
    regexPattern: "",
    dictionaryWords: [],
    likelihood: "LIKELY",
    enabled: true,
  });
  let dictionaryWordsText = $state("");
  let formErrors = $state<Record<string, string>>({});
  let submitting = $state(false);

  const LIKELIHOOD_OPTIONS: { value: CustomInfoTypeLikelihood; label: string }[] = [
    { value: "VERY_LIKELY", label: "Very Likely" },
    { value: "LIKELY", label: "Likely" },
    { value: "POSSIBLE", label: "Possible" },
    { value: "UNLIKELY", label: "Unlikely" },
    { value: "VERY_UNLIKELY", label: "Very Unlikely" },
  ];

  const DETECTION_TYPE_OPTIONS: { value: CustomInfoTypeDetectionType; label: string }[] = [
    { value: "regex", label: "Regular Expression" },
    { value: "dictionary", label: "Word List" },
  ];

  $effect(() => {
    if ($selectedOrgStore?.id) {
      loadCustomInfoTypes();
    }
  });

  async function loadCustomInfoTypes() {
    if (!$selectedOrgStore?.id) return;

    loading = true;
    error = null;
    try {
      customInfoTypes = await listCustomInfoTypes($selectedOrgStore.id);
    } catch (e) {
      error = e instanceof Error ? e.message : "Failed to load custom info types";
      console.error("Failed to load custom info types:", e);
    } finally {
      loading = false;
    }
  }

  function resetForm() {
    formData = {
      name: "",
      displayName: "",
      description: "",
      detectionType: "regex",
      regexPattern: "",
      dictionaryWords: [],
      likelihood: "LIKELY",
      enabled: true,
    };
    dictionaryWordsText = "";
    formErrors = {};
    editingId = null;
    showForm = false;
  }

  function openCreateForm() {
    resetForm();
    showForm = true;
  }

  function openEditForm(infoType: CustomInfoType) {
    editingId = infoType.id;
    formData = {
      name: infoType.name,
      displayName: infoType.displayName,
      description: infoType.description || "",
      detectionType: infoType.detectionType,
      regexPattern: infoType.regexPattern || "",
      dictionaryWords: infoType.dictionaryWords || [],
      likelihood: infoType.likelihood,
      enabled: infoType.enabled,
    };
    dictionaryWordsText = (infoType.dictionaryWords || []).join("\n");
    formErrors = {};
    showForm = true;
  }

  function validateForm(): boolean {
    formErrors = {};

    if (!formData.name) {
      formErrors.name = "Name is required";
    } else if (!/^[A-Z][A-Z0-9_]*$/.test(formData.name)) {
      formErrors.name = "Name must be uppercase letters, numbers, and underscores, starting with a letter";
    }

    if (!formData.displayName) {
      formErrors.displayName = "Display name is required";
    }

    if (formData.detectionType === "regex") {
      if (!formData.regexPattern) {
        formErrors.regexPattern = "Regex pattern is required";
      } else {
        try {
          new RegExp(formData.regexPattern);
        } catch {
          formErrors.regexPattern = "Invalid regular expression";
        }
      }
    } else if (formData.detectionType === "dictionary") {
      const words = dictionaryWordsText.split("\n").filter((w) => w.trim());
      if (words.length === 0) {
        formErrors.dictionaryWords = "At least one word is required";
      }
    }

    return Object.keys(formErrors).length === 0;
  }

  async function handleSubmit() {
    if (!$selectedOrgStore?.id || !validateForm()) return;

    submitting = true;
    try {
      // Prepare dictionary words from text
      if (formData.detectionType === "dictionary") {
        formData.dictionaryWords = dictionaryWordsText.split("\n").filter((w) => w.trim());
      }

      if (editingId) {
        await updateCustomInfoType($selectedOrgStore.id, editingId, {
          displayName: formData.displayName,
          description: formData.description,
          regexPattern: formData.detectionType === "regex" ? formData.regexPattern : undefined,
          dictionaryWords: formData.detectionType === "dictionary" ? formData.dictionaryWords : undefined,
          likelihood: formData.likelihood,
          enabled: formData.enabled,
        });
        toaster.success("Success", "Custom info type updated successfully");
      } else {
        await createCustomInfoType($selectedOrgStore.id, formData);
        toaster.success("Success", "Custom info type created successfully");
      }

      await loadCustomInfoTypes();
      resetForm();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Operation failed";
      toaster.error("Error", message);
    } finally {
      submitting = false;
    }
  }

  async function handleDelete(infoType: CustomInfoType) {
    if (!$selectedOrgStore?.id) return;
    if (!confirm(`Are you sure you want to delete "${infoType.displayName}"?`)) return;

    try {
      await deleteCustomInfoType($selectedOrgStore.id, infoType.id);
      toaster.success("Success", "Custom info type deleted successfully");
      await loadCustomInfoTypes();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to delete";
      toaster.error("Error", message);
    }
  }

  async function handleToggle(infoType: CustomInfoType) {
    if (!$selectedOrgStore?.id) return;

    try {
      await toggleCustomInfoType($selectedOrgStore.id, infoType.id, !infoType.enabled);
      await loadCustomInfoTypes();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to toggle";
      toaster.error("Error", message);
    }
  }
</script>

<div class="flex flex-col gap-6">
  <div class="flex justify-between items-center">
    <div>
      <h2 class="text-xl font-semibold">Custom Sensitive Data Types</h2>
      <p class="text-sm text-muted-foreground">
        Define custom patterns to detect organization-specific sensitive data in your documents.
      </p>
    </div>
    {#if !showForm}
      <Button onclick={openCreateForm} size="sm">
        <Plus class="w-4 h-4 mr-2" />
        Add Custom Type
      </Button>
    {/if}
  </div>

  {#if showForm}
    <div class="border rounded-lg p-6 bg-card">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-medium">
          {editingId ? "Edit Custom Type" : "Create Custom Type"}
        </h3>
        <button type="button" onclick={resetForm} class="text-muted-foreground hover:text-foreground">
          <X class="w-5 h-5" />
        </button>
      </div>

      <div class="grid gap-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            name="name"
            label="Name"
            placeholder="e.g., INTERNAL_PROJECT_CODE"
            bind:value={formData.name}
            error={formErrors.name || ""}
            disabled={!!editingId}
          />
          <Input
            name="displayName"
            label="Display Name"
            placeholder="e.g., Internal Project Code"
            bind:value={formData.displayName}
            error={formErrors.displayName || ""}
          />
        </div>

        <Input
          name="description"
          label="Description (optional)"
          placeholder="Describe what this type detects"
          bind:value={formData.description}
        />

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span class="text-sm font-medium mb-2 block">Detection Type</span>
            <Select.Root
              selected={DETECTION_TYPE_OPTIONS.find((o) => o.value === formData.detectionType)}
              onSelectedChange={(selected) => {
                if (selected) formData.detectionType = selected.value as CustomInfoTypeDetectionType;
              }}
              disabled={!!editingId}
            >
              <Select.Trigger name="detectionType">
                <Select.Value placeholder="Select detection type" />
              </Select.Trigger>
              <Select.Content>
                <Select.Group>
                  {#each DETECTION_TYPE_OPTIONS as option}
                    <Select.Item
                      value={option.value}
                      label={option.label}
                      isSelected={option.value === formData.detectionType}
                    >
                      {option.label}
                    </Select.Item>
                  {/each}
                </Select.Group>
              </Select.Content>
            </Select.Root>
          </div>

          <div>
            <span class="text-sm font-medium mb-2 block">Likelihood</span>
            <Select.Root
              selected={LIKELIHOOD_OPTIONS.find((o) => o.value === formData.likelihood)}
              onSelectedChange={(selected) => {
                if (selected) formData.likelihood = selected.value as CustomInfoTypeLikelihood;
              }}
            >
              <Select.Trigger name="likelihood">
                <Select.Value placeholder="Select likelihood" />
              </Select.Trigger>
              <Select.Content>
                <Select.Group>
                  {#each LIKELIHOOD_OPTIONS as option}
                    <Select.Item
                      value={option.value}
                      label={option.label}
                      isSelected={option.value === formData.likelihood}
                    >
                      {option.label}
                    </Select.Item>
                  {/each}
                </Select.Group>
              </Select.Content>
            </Select.Root>
          </div>
        </div>

        {#if formData.detectionType === "regex"}
          <div>
            <Input
              name="regexPattern"
              label="Regular Expression Pattern"
              placeholder="e.g., PRJ-[A-Z]{3}-\d{4}"
              bind:value={formData.regexPattern}
              error={formErrors.regexPattern || ""}
            />
            <p class="text-xs text-muted-foreground mt-1">
              Use standard regex syntax. Example: PRJ-[A-Z]{3}-\d{4} matches "PRJ-ABC-1234"
            </p>
          </div>
        {:else}
          <div>
            <label for="dictionaryWords" class="text-sm font-medium mb-2 block">
              Word List (one per line)
              {#if formErrors.dictionaryWords}
                <span class="text-destructive ml-2">{formErrors.dictionaryWords}</span>
              {/if}
            </label>
            <textarea
              id="dictionaryWords"
              class={cn(
                "w-full min-h-[120px] px-3 py-2 border rounded-md text-sm",
                "focus:outline-none focus:ring-2 focus:ring-ring",
                formErrors.dictionaryWords && "border-destructive"
              )}
              placeholder="Enter words or phrases, one per line"
              bind:value={dictionaryWordsText}
            ></textarea>
            <p class="text-xs text-muted-foreground mt-1">
              Enter words or phrases that should be detected as sensitive data.
            </p>
          </div>
        {/if}

        <div class="flex items-center gap-2">
          <input
            type="checkbox"
            id="enabled"
            bind:checked={formData.enabled}
            class="w-4 h-4 rounded border-gray-300"
          />
          <label for="enabled" class="text-sm">Enable this custom type for scanning</label>
        </div>

        <div class="flex justify-end gap-2 pt-4">
          <Button variant="outline" onclick={resetForm} disabled={submitting}>Cancel</Button>
          <Button onclick={handleSubmit} disabled={submitting}>
            {#if submitting}
              <span class="w-4 h-4 mr-2"><LoadingSpinner /></span>
            {/if}
            {editingId ? "Update" : "Create"}
          </Button>
        </div>
      </div>
    </div>
  {/if}

  {#if loading}
    <div class="flex justify-center py-8">
      <div class="w-8 h-8"><LoadingSpinner /></div>
    </div>
  {:else if error}
    <div class="flex items-center gap-2 text-destructive p-4 border border-destructive/20 rounded-lg">
      <AlertCircle class="w-5 h-5" />
      <span>{error}</span>
    </div>
  {:else if customInfoTypes.length === 0}
    <div class="text-center py-12 border rounded-lg bg-muted/50">
      <p class="text-muted-foreground mb-4">No custom sensitive data types defined yet.</p>
      <Button onclick={openCreateForm} variant="outline">
        <Plus class="w-4 h-4 mr-2" />
        Create Your First Custom Type
      </Button>
    </div>
  {:else}
    <div class="border rounded-lg overflow-hidden">
      <table class="w-full">
        <thead class="bg-muted/50">
          <tr>
            <th class="px-4 py-3 text-left text-sm font-medium">Name</th>
            <th class="px-4 py-3 text-left text-sm font-medium">Type</th>
            <th class="px-4 py-3 text-left text-sm font-medium">Pattern/Words</th>
            <th class="px-4 py-3 text-left text-sm font-medium">Status</th>
            <th class="px-4 py-3 text-right text-sm font-medium">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y">
          {#each customInfoTypes as infoType}
            <tr class="hover:bg-muted/30">
              <td class="px-4 py-3">
                <div class="font-medium">{infoType.displayName}</div>
                <div class="text-xs text-muted-foreground font-mono">{infoType.name}</div>
              </td>
              <td class="px-4 py-3 text-sm">
                {infoType.detectionType === "regex" ? "Regex" : "Dictionary"}
              </td>
              <td class="px-4 py-3 text-sm">
                {#if infoType.detectionType === "regex"}
                  <code class="text-xs bg-muted px-1 py-0.5 rounded">{infoType.regexPattern}</code>
                {:else}
                  <span class="text-muted-foreground">
                    {infoType.dictionaryWords?.length || 0} words
                  </span>
                {/if}
              </td>
              <td class="px-4 py-3">
                <button
                  type="button"
                  onclick={() => handleToggle(infoType)}
                  class={cn(
                    "px-2 py-1 text-xs rounded-full font-medium",
                    infoType.enabled
                      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                      : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                  )}
                >
                  {infoType.enabled ? "Enabled" : "Disabled"}
                </button>
              </td>
              <td class="px-4 py-3">
                <div class="flex justify-end gap-2">
                  <button
                    type="button"
                    onclick={() => openEditForm(infoType)}
                    class="p-1 text-muted-foreground hover:text-foreground"
                    title="Edit"
                  >
                    <Pencil class="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onclick={() => handleDelete(infoType)}
                    class="p-1 text-muted-foreground hover:text-destructive"
                    title="Delete"
                  >
                    <Trash2 class="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</div>
